// noinspection JSXUnresolvedComponent

import { isValidElement } from "react"
import useAsync from "lib/@hooks/useAsync"
import { Box } from "@mui/material"
import { Frag } from "lib/@components/slot/frag"
import { checkPermission } from "library/authorization/index.mjs"
import LoadingScreen from "minimals-template/components/LoadingScreen"

const imported = {}

const CHUNK_RELOAD_FLAG = "sfg20:chunk-reload"
const CHUNK_ERROR_PATTERNS = [
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "Importing a module script failed",
    "Expected a JavaScript-or-Wasm module script",
]

/**
 * Dynamically loads a component using the provided importer function and displays
 * various fallback states during the loading process.
 *
 * @param {Object} options Options for the Lazy function.
 * @param {string} options.$lazy Magic function to write the importer and extract the component using a string which is
 *     FILEPATH|EXTRACT
 * @param {function} options.importer Function to dynamically import the required module.
 * @param {string} options.id Identifier related to the component to be loaded.
 * @param {string} [options.extract="default"] The key of the exported component in the module.
 * @param {JSX.Element} [options.fallback=<Frag />] Fallback component to display during loading or error state.
 * @param {JSX.Element} [options.nullFallback=fallback] Fallback component to display if the extracted component is
 *     null.
 * @param {JSX.Element} [options.loadingScreen=<LoadingScreen />] Component to display while the module is being
 *     loaded.
 * @param {Array} [options.demands] List of demands/permissions required to load the component.
 * @param {boolean} [options.matchAllDemands=true] Whether all demands must be met (true) or just one (false).
 * @param {Object} props Additional properties to be passed to the imported component.
 *
 * @return {JSX.Element} The dynamically loaded component or a fallback component based on the loading state.
 *
 */
export function Lazy({
    importer,
    id,
    extract = "default",
    fallback = <Frag />,
    nullFallback = fallback,
    loadingScreen = <LoadingScreen />,
    demands,
    matchAllDemands = true,
    ...props
}) {
    const component = useAsync(
        async (update) => {
            const key = importer.toString()
            if (!imported[key]) {
                update(loadingScreen)
            }
            try {
                const module = await importer({ id, extract, fallback, ...props })
                imported[key] = true
                clearReloadFlag()
                const Component = module?.[extract || "default"]
                if (Component) {
                    return <Component />
                }
                return nullFallback
            } catch (e) {
                delete imported[key]
                if (isChunkLoadError(e) && attemptReloadOnce()) {
                    return fallback
                }
                throw e
            }
        },
        fallback,
        [id, extract]
    )

    if (demands) {
        if (!checkPermission(demands, matchAllDemands)) {
            return fallback
        }
    }

    if (component instanceof Error) {
        console.error("Lazy component failed to load:", component)
        return <Box color="error.main">Error loading component: {component.message}</Box>
    }

    if (!isValidElement(component)) {
        return nullFallback ?? fallback
    }
    return <component.type {...component.props} {...props} />
}

export function makeLazy(importer, extract, loadingScreen) {
    return function makeLazy(props = {}) {
        return (
            <Lazy
                importer={importer}
                nullFallback={<Box>Not Loaded</Box>}
                extract={extract}
                loadingScreen={loadingScreen}
                {...props}
            />
        )
    }
}

// Recovery for stale dynamic imports after a deploy: when a hashed chunk
// has been removed from S3, CloudFront returns index.html in its place,
// which surfaces as a chunk-load error. One forced reload swaps in the
// new index.html and the hashes it currently references.
function isChunkLoadError(error) {
    const message = error?.message ?? ""
    return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

function attemptReloadOnce() {
    try {
        if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return false
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()))
    } catch {
        return false
    }

    window.location.reload()
    return true
}

function clearReloadFlag() {
    try {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
    } catch {
        // sessionStorage unavailable; nothing to do
    }
}
